import React, { memo, useMemo } from 'react';
import { Typography, Box, Grid, Divider } from '@mui/material';
import { useSelector } from 'react-redux';
import { toLocaleStringIn } from '../../util/general';
import { Holding } from '../../reducer/holdings.reducer';
import { getSecurityKey } from '../../util/security';
import { selectPositionLtpsSignature } from '../../selectors/security';
import { buildLtpsByKey, getHoldingsSecurityKeys } from '../../util/positionLtpsSignature';
import { RootState } from '../../store';

interface HoldingsHeaderProps {
  holdings: Holding[];
}

const HoldingsHeader: React.FC<HoldingsHeaderProps> = ({ holdings }) => {
  const securityKeys = useMemo(() => getHoldingsSecurityKeys(holdings), [holdings]);
  const ltpsSignature = useSelector(selectPositionLtpsSignature(securityKeys));
  const ltpsByKey = useMemo(() => buildLtpsByKey(securityKeys, ltpsSignature), [securityKeys, ltpsSignature]);
  const previousCloseSignature = useSelector((state: RootState) =>
    securityKeys.map((key) => state.securityStore.snapshots[key]?.previousClose ?? 0).join('\0')
  );
  const previousCloseByKey = useMemo(() => buildLtpsByKey(securityKeys, previousCloseSignature), [securityKeys, previousCloseSignature]);

  let totalInvestedAmount = 0;
  let totalCurrentValue = 0;
  let totalDayPnl = 0;
  let totalPreviousCloseValue = 0;

  holdings.forEach((holding) => {
    if (!holding.security) return;
    const key = getSecurityKey(holding.security.exchange, holding.security.code);
    const multiplier = holding.security.priceMultiplier ?? 100;
    const ltp = (ltpsByKey.get(key) ?? 0) / multiplier;
    const averageBuyPrice = holding.averageBuyPrice / multiplier;
    const quantity = holding.quantity;
    const previousClose = (previousCloseByKey.get(key) ?? 0) / multiplier;

    if (ltp && averageBuyPrice && quantity) {
      totalInvestedAmount += averageBuyPrice * quantity;
      totalCurrentValue += ltp * quantity;
      totalDayPnl += (ltp - previousClose) * quantity;
      totalPreviousCloseValue += previousClose * quantity;
    }
  });

  const totalDayChangePercentage = (totalDayPnl / totalPreviousCloseValue) * 100;
  const totalPnl = totalCurrentValue - totalInvestedAmount;

  if (totalInvestedAmount <= 0) {
    return null;
  }

  return (
    <>
    <Box sx={{ padding: '12px' }}>
      <Grid container spacing={1} alignItems="center">
        <Grid item xs={6} sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="textSecondary">
            Invested
          </Typography>
          <Typography variant="subtitle2">
            {toLocaleStringIn(totalInvestedAmount)}
          </Typography>
        </Grid>
        <Grid item xs={6} sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="textSecondary">
            Current
          </Typography>
          <Typography variant="subtitle2">
            {toLocaleStringIn(totalCurrentValue)}
          </Typography>
        </Grid>
        <Grid item xs={6} sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="textSecondary">
            P&L
          </Typography>
          <Typography variant="subtitle2" className={totalPnl < 0 ? 'red-text' : 'green-text'} fontSize={'16px'}>
            {toLocaleStringIn(totalPnl)}
              <span style={{ fontSize: '12px', marginLeft: '4px'}}>
                {totalInvestedAmount > 0 ? (
                  `${totalPnl >= 0 ? '(+' : '('}${((totalPnl / totalInvestedAmount) * 100).toFixed(2)}%)`
                ) : (
                  '(--)'
                )}
              </span>
          </Typography>
        </Grid>
          <Grid item xs={6} sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="textSecondary">
                  Day's P&L
              </Typography>
              <Typography variant="subtitle2" className={totalDayPnl < 0 ? 'red-text' : 'green-text'} fontSize={'16px'}>
                  {toLocaleStringIn(totalDayPnl)}
                  <span style={{ fontSize: '12px', marginLeft: '4px'}}>
                {Number.isFinite(totalDayChangePercentage) ? (
                  `${totalDayChangePercentage >= 0 ? '(+' : '('}${totalDayChangePercentage.toFixed(2)}%)`
                ) : (
                  '(--)'
                )}
              </span>
              </Typography>
          </Grid>
      </Grid>
    </Box>
    <Divider style={{ width: '100%', height: '2px' }} />
    </>
  );
};

export default memo(
  HoldingsHeader,
  (prev, next) => prev.holdings === next.holdings
);
